/**
 * F2 — Produtos por vendorId com paginação (sem teto global).
 * mode 'live'  = onSnapshot na página 1 (dashboard) + getDocs no loadMore
 * mode 'once'  = getDocs acumulando páginas (vitrine)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  documentId,
  getCountFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
  type QueryDocumentSnapshot,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import type { Product } from '../types';

export const SCOPED_PRODUCTS_PAGE_SIZE = 50;

export type ScopedProductsMode = 'live' | 'once';

export interface UseScopedProductsOptions {
  mode?: ScopedProductsMode;
  pageSize?: number;
}

export interface UseScopedProductsResult {
  products: Product[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  reload: () => void;
  /** Contagem agregada (1 read) — evita full scan para badge total */
  totalCount: number | null;
  /** Leituras de página nesta sessão (debug / D3) */
  sessionReads: number;
}

function mapDocs(docs: QueryDocumentSnapshot<DocumentData>[]): Product[] {
  return docs.map((d) => ({ id: d.id, ...d.data() } as Product));
}

function mergeById(prev: Product[], next: Product[]): Product[] {
  const map = new Map<string, Product>();
  for (const p of prev) map.set(p.id, p);
  for (const p of next) map.set(p.id, p);
  return Array.from(map.values());
}

function buildCountQuery(vendorId: string) {
  return query(collection(db, 'products'), where('vendorId', '==', vendorId));
}

type PageCacheEntry = {
  products: Product[];
  lastDoc: QueryDocumentSnapshot<DocumentData>;
  hasMore: boolean;
};

/** Cache por vendor — sobrevive remount do hook na mesma sessão SPA (D3). */
const vendorPageCache = new Map<string, Map<string, PageCacheEntry>>();

function getVendorPageCache(vendorId: string): Map<string, PageCacheEntry> {
  let cache = vendorPageCache.get(vendorId);
  if (!cache) {
    cache = new Map();
    vendorPageCache.set(vendorId, cache);
  }
  return cache;
}

function cacheKeyFor(cursor: QueryDocumentSnapshot<DocumentData> | null): string {
  return cursor ? cursor.id : '__first__';
}

function buildPageQuery(
  vendorId: string,
  pageSize: number,
  cursor: QueryDocumentSnapshot<DocumentData> | null
) {
  if (cursor) {
    return query(
      collection(db, 'products'),
      where('vendorId', '==', vendorId),
      orderBy(documentId()),
      startAfter(cursor),
      limit(pageSize)
    );
  }
  return query(
    collection(db, 'products'),
    where('vendorId', '==', vendorId),
    orderBy(documentId()),
    limit(pageSize)
  );
}

export function useScopedProducts(
  vendorId: string | null,
  opts?: UseScopedProductsOptions
): UseScopedProductsResult {
  const mode: ScopedProductsMode = opts?.mode ?? 'once';
  const pageSize = opts?.pageSize ?? SCOPED_PRODUCTS_PAGE_SIZE;

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [sessionReads, setSessionReads] = useState(0);

  const lastDocRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  /** Docs da página 1 (live) — para merge com extras do loadMore */
  const page1IdsRef = useRef<Set<string>>(new Set());
  const extrasRef = useRef<Product[]>([]);
  const unsubRef = useRef<Unsubscribe | null>(null);
  const loadingMoreRef = useRef(false);
  const pageCacheRef = useRef<Map<string, PageCacheEntry>>(getVendorPageCache(vendorId || ''));

  const cleanupListener = useCallback(() => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
  }, []);

  const reload = useCallback(() => {
    if (vendorId) vendorPageCache.delete(vendorId);
    setReloadKey((k) => k + 1);
  }, [vendorId]);

  useEffect(() => {
    cleanupListener();
    lastDocRef.current = null;
    page1IdsRef.current = new Set();
    extrasRef.current = [];
    if (vendorId) {
      pageCacheRef.current = getVendorPageCache(vendorId);
    } else {
      pageCacheRef.current = new Map();
    }
    setProducts([]);
    setError(null);
    setHasMore(false);
    setTotalCount(null);
    setSessionReads(0);

    if (!vendorId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    const q = buildPageQuery(vendorId, pageSize, null);

    getCountFromServer(buildCountQuery(vendorId))
      .then((snap) => {
        if (!cancelled) setTotalCount(snap.data().count);
      })
      .catch(() => {
        if (!cancelled) setTotalCount(null);
      });

    if (mode === 'live') {
      const unsub = onSnapshot(
        q,
        (snap) => {
          if (cancelled) return;
          const docs = snap.docs;
          const page1 = mapDocs(docs);
          page1IdsRef.current = new Set(page1.map((p) => p.id));
          // Cursor para loadMore: fim da página 1, a menos que extras já tenham avançado
          if (extrasRef.current.length === 0) {
            lastDocRef.current = docs.length > 0 ? docs[docs.length - 1] : null;
            setHasMore(docs.length >= pageSize);
          }
          setProducts(mergeById(page1, extrasRef.current));
          setLoading(false);
          setError(null);
        },
        (err) => {
          if (cancelled) return;
          console.error('[useScopedProducts] live error', err);
          setError(err?.message || 'Erro ao carregar produtos');
          setLoading(false);
          setHasMore(false);
        }
      );
      unsubRef.current = unsub;
      return () => {
        cancelled = true;
        cleanupListener();
      };
    }

    getDocs(q)
      .then((snap) => {
        if (cancelled) return;
        const docs = snap.docs;
        lastDocRef.current = docs.length > 0 ? docs[docs.length - 1] : null;
        const page = mapDocs(docs);
        setProducts(page);
        setHasMore(docs.length >= pageSize);
        setSessionReads(1);
        if (docs.length > 0) {
          pageCacheRef.current.set('__first__', {
            products: page,
            lastDoc: docs[docs.length - 1],
            hasMore: docs.length >= pageSize,
          });
        }
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[useScopedProducts] once error', err);
        setError(err?.message || 'Erro ao carregar produtos');
        setLoading(false);
        setHasMore(false);
      });

    return () => {
      cancelled = true;
    };
  }, [vendorId, mode, pageSize, reloadKey, cleanupListener]);

  const loadMore = useCallback(async () => {
    if (!vendorId || !hasMore || loadingMoreRef.current) return;
    if (!lastDocRef.current) return;

    const cursor = lastDocRef.current;
    const cacheKey = cacheKeyFor(cursor);
    const cached = pageCacheRef.current.get(cacheKey);
    if (cached) {
      lastDocRef.current = cached.lastDoc;
      setHasMore(cached.hasMore);
      if (mode === 'live') {
        extrasRef.current = mergeById(extrasRef.current, cached.products);
        setProducts((prev) => mergeById(prev, cached.products));
      } else {
        setProducts((prev) => mergeById(prev, cached.products));
      }
      return;
    }

    loadingMoreRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const q = buildPageQuery(vendorId, pageSize, cursor);
      const snap = await getDocs(q);
      setSessionReads((n) => n + 1);
      const docs = snap.docs;
      const next = mapDocs(docs);
      const nextHasMore = docs.length >= pageSize;
      if (docs.length > 0) {
        const lastDoc = docs[docs.length - 1];
        lastDocRef.current = lastDoc;
        pageCacheRef.current.set(cacheKey, {
          products: next,
          lastDoc,
          hasMore: nextHasMore,
        });
      }
      if (mode === 'live') {
        extrasRef.current = mergeById(extrasRef.current, next);
        setProducts((prev) => mergeById(prev, next));
      } else {
        setProducts((prev) => mergeById(prev, next));
      }
      setHasMore(nextHasMore);
    } catch (err: any) {
      console.error('[useScopedProducts] loadMore error', err);
      setError(err?.message || 'Erro ao carregar mais produtos');
    } finally {
      loadingMoreRef.current = false;
      setLoading(false);
    }
  }, [vendorId, hasMore, pageSize, mode]);

  return { products, loading, error, hasMore, loadMore, reload, totalCount, sessionReads };
}

/** Helpers puros para vitest (sem Firestore). */
export const __scopedProductsTestUtils = {
  mergeById,
  clearVendorPageCache: (vendorId?: string) => {
    if (vendorId) vendorPageCache.delete(vendorId);
    else vendorPageCache.clear();
  },
};
